<template>
  <AdminLayout>
    <template #content>
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Billetage de caisse ({{ totalRows }})</h1>
          <small class="text-muted">
            Un seul billetage par jour et par utilisateur. À faire en fin de service avant passation ou fermeture.
          </small>
        </div>

        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['cash_counts', 'CashCount', 'CashCountLine']" label="Billetage de caisse" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>

        </div>
      </div>

      <!-- Messages -->
      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- Filtres -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div class="col-md-3">
              <label class="form-label small mb-1">Du</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="filters.date_from"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-1">Au</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="filters.date_to"
              />
            </div>
            <div class="col-md-3">
              <label class="form-label small mb-1">Statut</label>
              <select
                class="form-select form-select-sm"
                v-model="filters.status"
              >
                <option value="">Tous</option>
                <option value="draft">Brouillon</option>
                <option value="validated">Validé</option>
              </select>
            </div>
            <div class="col-12 module-filter-actions mt-2">
              <button
                class="btn btn-outline-secondary btn-sm btn-icon-label"
                type="button"
                @click="resetFilters"
              >
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button
                class="btn btn-primary btn-sm btn-icon-label"
                type="button"
                @click="fetchCashCounts"
              >
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tableau -->
      <BootstrapDataTable
        :rows="cashCounts"
        :columns="tableColumns"
        :loading="isLoading"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :active-filter-count="activeFilterCount"
        state-key="cash-counts.list"
        detail-route-name="CashCountShow"
        empty-text="Aucun billetage trouvé."
        @sort-change="handleSortChange"
        @refresh="fetchCashCounts"
      >
        <template #cell-operation_date="{ row }">
          {{ $format.date(row.operation_date) }}
        </template>

        <template #cell-status="{ row }">
          <span
            class="badge"
            :class="row.status === 'validated' ? 'bg-success' : 'bg-secondary'"
          >
            {{ row.status === 'validated' ? 'Validé' : 'Brouillon' }}
          </span>
        </template>

        <template #[`cell-user.name`]="{ row }">
          <span v-if="row.user">
            {{ row.user.name }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell-expected_amount="{ row }">
          {{ $format.currency(row.expected_amount) }}
        </template>

        <template #cell-counted_amount="{ row }">
          {{ $format.currency(row.counted_amount) }}
        </template>

        <template #cell-difference="{ row }">
          <span
            :class="{
              'text-success': (row.difference || 0) > 0,
              'text-danger': (row.difference || 0) < 0,
              'text-muted': (row.difference || 0) === 0,
            }"
          >
            {{ $format.currency(row.difference) }}
          </span>
        </template>

        <template #cell-created_at="{ row }">
          {{ $format.datetime(row.created_at) }}
        </template>
      </BootstrapDataTable>

    </template>
  </AdminLayout>
</template>

<script>
import {
  countActiveFilters,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';

const FILTER_MODULE = 'cash-counts';
const DEFAULT_FILTERS = {
  date_from: '',
  date_to: '',
  status: '',
};

export default {
  name: 'CashCountsView',
  data() {
    return {
      cashCounts: [],
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      filters: {
        ...DEFAULT_FILTERS,
      },
      filtersVisible: false,
      table: {
        sortBy: 'operation_date',
        sortDir: 'desc',
      },
      tableColumns: [
        { key: 'operation_date', label: 'Date', sortable: true },
        { key: 'status', label: 'Statut', sortable: true },
        { key: 'user.name', label: 'Utilisateur', sortable: true },
        { key: 'expected_amount', label: 'Théorique', sortable: true, class: 'text-end' },
        { key: 'counted_amount', label: 'Compté', sortable: true, class: 'text-end' },
        { key: 'difference', label: 'Différence', sortable: true, class: 'text-end' },
        { key: 'created_at', label: 'Créé le', sortable: true },
      ],
    };
  },
  mounted() {
    this.restoreFilters();
    this.fetchCashCounts();
  },
  computed: {
    totalRows() {
      return this.cashCounts.length;
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, this.defaultFilters());
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, this.defaultFilters());
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },

    defaultFilters() {
      const today = new Date().toISOString().slice(0, 10);

      return {
        ...DEFAULT_FILTERS,
        date_from: today,
        date_to: today,
      };
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    initDefaultDates() {
      const today = new Date().toISOString().slice(0, 10);
      this.filters.date_from = today;
      this.filters.date_to = today;
    },

    async fetchCashCounts() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const res = await this.$api.get('/cash-counts', {
          params: {
            date_from: this.filters.date_from || undefined,
            date_to: this.filters.date_to || undefined,
            status: this.filters.status || undefined,
          },
        });

        // Paginator possible : data.data.data ou data.data
        const payload = res.data.data;
        this.cashCounts =
          (payload && payload.data) ||
          payload ||
          res.data.data ||
          [];

      } catch (e) {
        console.error('Erreur chargement billetages', e);
        this.errorMessage = 'Impossible de charger les billetages.';
      } finally {
        this.isLoading = false;
      }
    },

    resetFilters() {
      this.filters.status = '';
      this.initDefaultDates();
      this.fetchCashCounts();
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
    },
  },
};
</script>
